import React, { useEffect, useState } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import CssBaseline from '@mui/material/CssBaseline'
import DashboardLayout from "./Components/Dashboard/DashboardLayout";
import "./App.css";
import { NotFound } from "./FrontEnd/NotFound";
import ProtectedRoute from "./ProtectedRoute";

//route imports;
// import MyAccount from "./FrontEnd/MyAccount/MyAccount";
import Unauthorized from "./FrontEnd/Unauthorized";
import LoginForm from "./FrontEnd/Authentication/Login";
import http from "./commonFunctions/httpClient";
import { useAuth } from "./StateManagement/authContext";
import { setAuthContext } from "./commonFunctions/util";
import SplashScreen from "./Components/wsSpashScreen";
import SettingsPage from "./FrontEnd/Settings/Settings";
import BlockManager from "./FrontEnd/Common/BlockManager";
import CDSManager from "./FrontEnd/Common/CdsManager";
import { MainSwitch } from "./FrontEnd/Common/MainSwitch";
import Loan from "./FrontEnd/Loans/Loan";
import LoanList from "./FrontEnd/Loans/LoanList";
import UserList from "./FrontEnd/Users/UserList";
import UserRegistration from "./FrontEnd/Users/UserRegistration";
import UserDetailPage from "./FrontEnd/Users/UserDetail";
import UnauthorizedLogs from "./FrontEnd/Users/UnAuthorizedLogs";
import AboutPage from "./FrontEnd/Common/About";
import NotificationPanel from "./FrontEnd/Notifications/Notifications";
import LoanActivityLog from "./FrontEnd/Loans/LoanActivity";
import CreateRepayment from "./FrontEnd/Repayment/CreateRepayment";
import LoanDetailPage from "./FrontEnd/Loans/LoanDetail";
import { ActionReconcilationOrChequeList } from "./FrontEnd/Loans/ActionCompletedOrSanctioned";
import SanctionLoan from "./FrontEnd/Loans/Sanction/Sanction";
import { Reports } from "./FrontEnd/Reports/Reports";
import FilteredLoans from "./FrontEnd/Loans/FilteredLoans";
import AllLoans from "./FrontEnd/Loans/AllLoans";
import FilteredRepayments from "./FrontEnd/Repayment/FilteredRepayments";
import { DashBoard } from "./FrontEnd/Dashboard/Dashboard";
import DisbursedLoan from "./FrontEnd/Loans/Disbursed/DisbursedLoan";
import RepaymentDetailPage from "./FrontEnd/Repayment/RepaymentDetails";
import UploadLoans from "./FrontEnd/Loans/UploadLoan";

const App: React.FC = () => {
  const auth = useAuth();
  const location = useLocation();

  const [showSplash, setShowSplash] = useState(true);

  // useEffect(() => {
  //   const disableRightClick = (e: MouseEvent) => e.preventDefault();
  //   window.addEventListener('contextmenu', disableRightClick);
  //   return () => {
  //     window.removeEventListener('contextmenu', disableRightClick);
  //   };
  // }, []);


  useEffect(() => {
    const splashTimeout = setTimeout(() => {
      setShowSplash(false);
    }, 2000); // splash duration

    return () => clearTimeout(splashTimeout);
  }, []);


  useEffect(() => {
    if (location.pathname !== '/login') {
      setAuthContext(auth);
    }
  }, [auth, location.pathname]);

  useEffect(() => {
    let interval: any;

    const startInterval = () => {
      interval = setInterval(() => {
        http.get("auth/refresh-token").catch(() => {
          console.warn("Auto refresh failed");
        });
      }, 10 * 60 * 1000); //every 10 minutes;
    };

    if (document.visibilityState === "visible") {
      startInterval();
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        startInterval();
      } else {
        clearInterval(interval);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);



  if (auth.loading || showSplash) {
    return <SplashScreen />
  }

  return (
    <>
      <CssBaseline />
      <Routes>
        <Route
          path="/"
          element={
            auth.isAuthenticated ? (
              <DashboardLayout />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        >
          <Route index element={auth.user?.userType !== 'cds' ? <DashBoard /> : <LoanList />} />

          {/* All Authenticated Users */}
          <Route element={<ProtectedRoute isAuthenticated={auth.isAuthenticated} />}>
            <Route path="aboutus" element={<AboutPage />} />
            {/* <Route path="myAccount/:id" element={<MyAccount />} /> */}
            {/* <Route path="settings" element={<SettingsPage />} /> */}
            <Route path="loan/requests/list" element={<LoanList />} />
            <Route path="loan/requests/final/list" element={<ActionReconcilationOrChequeList />} />
            <Route path="loan/repayment/requests/list/filter/:loanId?" element={<FilteredRepayments />} />
            {/* <RepaymentDetailPage */}
            <Route path="loan/repayment/details/:repaymentId" element={<RepaymentDetailPage />} />
            <Route path="loan/detail/:id" element={<LoanDetailPage />} />
            <Route path="loan/requests/list/filter" element={<FilteredLoans />} />
            <Route path="loan/requests/list/all" element={<AllLoans />} />

            <Route path="reports" element={<Reports />} />
          </Route>

          {/* Admin Only */}
          <Route
            element={
              <ProtectedRoute
                isAuthenticated={auth.isAuthenticated}
                allowedRoles={["admin"]}
              />
            }
          >
            <Route path="manage" element={<MainSwitch />} />
            <Route path="upload/loans" element={<UploadLoans />} />
            <Route path="unauthorizedLogs/list" element={<UnauthorizedLogs />} />
          </Route>


          {/* District Only */}
          <Route
            element={
              <ProtectedRoute
                isAuthenticated={auth.isAuthenticated}
                allowedRoles={["district", "admin"]}
              />
            }
          >
            <Route path="loan/request/sanction/:loanId" element={<SanctionLoan />} />
            {/* <Route path="loan/request/disburse/:loanId" element={<DisbursedLoan />} /> 8/27/2025  updated for block to manage*/}
            {/* <Route path="loan/type/:id?" element={<LoanType />} />
            <Route path="loan/types/list" element={<LoanTypeList />} /> */}
            <Route path="loan/activityLogs/:loanId" element={<LoanActivityLog />} />

            {/* <Route path="user/registration/:id?" element={<UserRegistration />} /> */}
            <Route path="manage/blocks" element={<BlockManager />} />
          </Route>

          {/* Block Only */}
          <Route
            element={
              <ProtectedRoute
                isAuthenticated={auth.isAuthenticated}
                allowedRoles={["block", "admin"]}
              />
            }
          >
            <Route path="manage/cds" element={<CDSManager />} />
          </Route>

          <Route element={<ProtectedRoute isAuthenticated={auth.isAuthenticated} allowedRoles={['block']} />}>
            <Route path="loan/repayment/request/create/:loanId" element={<CreateRepayment />} />
            <Route path="loan/request/:id?" element={<Loan />} />
            <Route path="loan/request/disburse/:loanId" element={<DisbursedLoan />} />
          </Route>

          <Route element={<ProtectedRoute isAuthenticated={auth.isAuthenticated} allowedRoles={['block', 'district', 'admin']} />}>
            <Route path="users/list" element={<UserList />} />
            <Route path="user/detail/:id" element={<UserDetailPage />} />
            <Route path="dashboard" element={<DashBoard />} />
            <Route path="notifications" element={<NotificationPanel />} />
          </Route>

          {/* CDS Only */}
          <Route
            element={
              <ProtectedRoute
                isAuthenticated={auth.isAuthenticated}
                allowedRoles={["cds"]}
              />
            }
          >
            <Route path="my-applications" element={<LoanList />} />
          </Route>
        </Route>

        {/* Public Routes */}
        <Route path="/login" element={
          auth.isAuthenticated ? (
            <Navigate to="/" />
          ) : (
            <LoginForm />
          )
        } />
        <Route path="/unauthorized" element={<Unauthorized />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
};

export default App;